import {otsBaseApi} from "../../ots-api";
import {Person} from "../../../model/person";
import {removeUndefined} from "../../../util/util";
import {PersonState} from "../../../model/person-state";
import {driverActivityCacheUpdate} from "../driver-activity/driver-activity-store";
import {PersonCapability, PersonCapabilityRequestDTO} from "../../../model/person-capability";

const ALL_TAG_TYPES = [
  "Persons",
  "PersonCapabilities",
  "PersonsState",
  "ObjectObjectChangeLogs",
  "User"
] as const;

type TagType = typeof ALL_TAG_TYPES[number];

const taggedBaseApi = otsBaseApi.enhanceEndpoints({
  addTagTypes: ALL_TAG_TYPES
});

export const personApi = taggedBaseApi.injectEndpoints({
  endpoints: (builder) => ({
    getAllPersons: builder.query<Person[], string | undefined>({
      query: (regionId) => {

        const params: Record<string, any> | undefined = {
          regionId: regionId,
          withStateData: true
        }

        return {
          url: `/person/all`,
          method: "GET",
          params: removeUndefined(params)
        }
      },
      providesTags: (result) => {
        if (result) {
          return [
            ...result.map(({id}) => ({type: "Persons", id} as const)),
            {type: "Persons", id: "LIST"}
          ];
        } else {
          return [{type: "Persons", id: "LIST"}];
        }
      }
    }),
    getAllOperatorPersons: builder.query<Person[], string | undefined>({
      query: (regionId) => {

        const params: Record<string, any> | undefined = {
          regionId: regionId,
          withStateData: true
        }

        return {
          url: `/person/all/operator`,
          method: "GET",
          params: removeUndefined(params)
        }
      },
      providesTags: (result) => {
        if (result) {
          return [
            ...result.map(({id}) => ({type: "Persons", id} as const)),
            {type: "Persons", id: "LIST"}
          ];
        } else {
          return [{type: "Persons", id: "LIST"}];
        }
      }
    }),
    getPerson: builder.query<Person, string>({
      query: (id) => ({
        url: `/person/${id}`,
        method: "GET"
      }),
      providesTags: (result, error, id) => [{type: "Persons", id}]
    }),
    getUser: builder.query<Person, void>({
      query: (id) => ({
        url: `/person`,
        method: "GET"
      }),
      providesTags: (result, error, id) => [{type: "User", id: "LIST"}]
    }),
    getPersonState: builder.query<PersonState, { id: string, regionId?: string }>({
      query: ({id, regionId}) => ({
        url: `/person/state/${id}`,
        method: "GET",
        params: removeUndefined({
          regionId: regionId
        })
      }),
      providesTags: (result, error, {id}) => [
        {type: "Persons", id},
        {type: "PersonsState", id}
      ]
    }),
    createPerson: builder.mutation<Person, Partial<Person>>({
      query: (person) => ({
        url: '/person',
        method: 'POST',
        body: person,
      }),
      invalidatesTags: [{type: 'Persons', id: 'LIST'}],
    }),
    updatePerson: builder.mutation<Person, Partial<Person>>({
      query: (person) => ({
        url: '/person',
        method: 'PUT',
        body: person,
      }),
      invalidatesTags: (result, error, {id}) => [
        {type: 'Persons', id},
        {type: 'ObjectObjectChangeLogs', id}
      ],
      async onQueryStarted(_arg, {dispatch, queryFulfilled, getState}) {
        try {
          const {data} = await queryFulfilled
          driverActivityCacheUpdate(dispatch, getState(), (draft) => {
            const index = draft.findIndex(s => s.person?.id === data.id)
            if (index >= 0) {
              draft[index] = data
            }
          })
        } catch (error) {
          console.debug(error)
        }
      },
    }),
    deletePerson: builder.mutation<void, Partial<Person>>({
      query: (person) => ({
        url: '/person',
        method: 'DELETE',
        body: person,
      }),
      invalidatesTags: (result, error, {id}) => [{type: 'Persons', id}],
    }),
    /**
     * Changes a password. `oldPassword` is required for your own; admins resetting someone else's
     * omit it. Replaces the legacy /auth/change_password flow.
     */
    updatePersonPassword: builder.mutation<void, { personId: string, oldPassword?: string, password: string }>({
      query: ({personId, oldPassword, password}) => ({
        url: `/person/${personId}/password`,
        method: 'PUT',
        body: {oldPassword, password},
      }),
      invalidatesTags: (result, error, {personId}) => [
        {type: 'Persons', id: personId},
        {type: 'ObjectObjectChangeLogs', id: personId}
      ],
    }),
    getPersonCapabilities: builder.query<PersonCapability[], string>({
      query: (personId) => ({
        url: `/persons/${personId}/capabilities`,
        method: "GET",
      }),
      providesTags: (result, error, arg) => {
        return [
          {type: "PersonCapabilities", id: arg}
        ];
      }
    }),
    addCapabilitiesToPerson: builder.mutation<void, { personId: string, data: PersonCapabilityRequestDTO }>({
      query: (arg) => ({
        url: `/persons/${arg.personId}/capabilities`,
        method: "POST",
        body: arg.data,
      }),
      invalidatesTags: (result, error, {personId}) => [{type: "PersonCapabilities", id: personId}]
    }),
    removeCapabilitiesFromPerson: builder.mutation<void, { personId: string, data: PersonCapabilityRequestDTO }>({
      query: (arg) => ({
        url: `/persons/${arg.personId}/capabilities`,
        method: "DELETE",
        body: arg.data,
      }),
      invalidatesTags: (result, error, {personId}) => [{type: "PersonCapabilities", id: personId}]
    }),
  })
});

export const {
  useGetAllPersonsQuery,
  useGetAllOperatorPersonsQuery,
  useGetPersonQuery,
  useGetUserQuery,
  useLazyGetUserQuery,
  useGetPersonStateQuery,
  useCreatePersonMutation,
  useUpdatePersonMutation,
  useDeletePersonMutation,
  useUpdatePersonPasswordMutation,
  useGetPersonCapabilitiesQuery,
  useAddCapabilitiesToPersonMutation,
  useRemoveCapabilitiesFromPersonMutation,
} = personApi;
